import { MouseEvent, useCallback } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { HugeiconsIcon } from '@hugeicons/react'
import { useLingui } from '@lingui/react/macro'

import {
  Delete01Icon,
  PencilEdit01Icon,
  VolumeHighIcon,
  VolumeOffIcon
} from '@hugeicons/core-solid-rounded'

import AddCaptionsPopover from './AddCaptionsPopover'
import AltTextPopover from './AltTextPopover'

interface FileButtonsProps {
  showCaptions: boolean
  showReplace: boolean
  locale?: GraphQL.AppLocale
  captions?: GraphQL.Caption[] | null
  src: string
  onChangeCaptions: (captions: GraphQL.Caption[]) => void
  onEdit: () => void
  onRemove?: () => void

  // AltText for a18y
  showAltText: boolean
  altText?: string | null
  translations?: (GraphQL.TranslationInput | GraphQL.Translation)[]
  onChangeAltText: (
    altText: string | null,
    translations?: (GraphQL.TranslationInput | GraphQL.Translation)[]
  ) => void

  // Make it possible to mute videos for ever
  showMute?: boolean
  alwaysMute?: boolean | null
  onAlwaysMute?: () => void
}

export function FileButtons(props: FileButtonsProps) {
  const {
    showReplace,
    showCaptions,
    captions,
    src,
    locale,
    onChangeCaptions,
    onEdit,
    onRemove: onRemoveProxy,
    alwaysMute = false,
    showMute = false,
    onAlwaysMute,
    altText,
    showAltText,
    translations,
    onChangeAltText
  } = props

  const { t } = useLingui()

  const onRemove = useCallback(
    (event: MouseEvent) => {
      event.preventDefault()

      onRemoveProxy?.()
    },
    [onRemoveProxy]
  )

  return (
    <div className="absolute right-2 top-2 flex flex-row gap-2">
      {showReplace && (
        <button
          className="size-6 rounded bg-black/50 p-1 text-white outline-primary hover:bg-black/70"
          tabIndex={0}
          onClick={onEdit}
          type="button"
          aria-label={t`edit`}
        >
          <HugeiconsIcon icon={PencilEdit01Icon} className="size-full" />
        </button>
      )}
      {showMute && (
        <button
          className="size-6 rounded bg-black/50 p-1 text-white outline-primary hover:bg-black/70"
          tabIndex={0}
          onClick={onAlwaysMute}
          type="button"
          aria-label={alwaysMute ? t`Enable sounds` : t`Disable sounds`}
          title={alwaysMute ? t`Enable sounds` : t`Disable sounds`}
        >
          <HugeiconsIcon
            icon={alwaysMute ? VolumeOffIcon : VolumeHighIcon}
            className="size-full"
          />
        </button>
      )}
      {showCaptions && locale && (
        <div className="size-6">
          <AddCaptionsPopover
            locale={locale}
            captions={captions}
            src={src}
            onChange={onChangeCaptions}
          />
        </div>
      )}
      {showAltText && (
        <div className="size-6">
          <AltTextPopover
            altText={altText}
            translations={translations}
            onChange={onChangeAltText}
          />
        </div>
      )}
      {onRemoveProxy && (
        <button
          className="size-6 rounded bg-black/50 p-1 text-white outline-primary hover:bg-black/70"
          tabIndex={0}
          onClick={onRemove}
          type="button"
          aria-label={t`remove`}
        >
          <HugeiconsIcon icon={Delete01Icon} className="size-full" />
        </button>
      )}
    </div>
  )
}
